import { useId } from "react";

import {
  WinterfellBanner,
  WinterfellRoundTower,
} from "@/components/staffer-pets/playyard/winterfell";

const TOWERS = [
  { x: 657, y: 38, width: 122, height: 276, levels: 3 },
  { x: 825, y: 119, width: 88, height: 212, levels: 2 },
  { x: 452, y: 132, width: 100, height: 210, levels: 2 },
  { x: 733, y: 173, width: 77, height: 170, levels: 2 },
  { x: 344, y: 221, width: 75, height: 120, levels: 1 },
];
const HALLS = [
  { x: 286, y: 213, width: 98, height: 67 },
  { x: 497, y: 181, width: 107, height: 108 },
  { x: 850, y: 251, width: 84, height: 67 },
  { x: 548, y: 277, width: 100, height: 55 },
];
const GODSWOOD_PINES = [
  { x: 94, y: 258 },
  { x: 117, y: 244 },
  { x: 143, y: 267 },
  { x: 161, y: 237 },
  { x: 189, y: 251 },
  { x: 213, y: 267 },
  { x: 237, y: 246 },
  { x: 258, y: 269 },
];
const FRONT_WALL =
  "M273 292 482 313 735 298 953 278V337L736 361 482 371 273 346Z";

export const WinterfellCastle = ({ nightAmount }: { nightAmount: number }) => {
  /* React Hooks */
  const masonryId = useId();

  /* Render */
  const render = () => (
    <svg
      className="absolute left-0 top-[15%] h-[42%] w-[78%]"
      viewBox="0 0 1000 380"
      preserveAspectRatio="xMidYMax meet"
    >
      <defs>
        <pattern
          id={masonryId}
          width="24"
          height="12"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M0 0H24M0 12H24M12 0V6M0 6H24M5 6V12M21 6V12"
            fill="none"
            stroke="#132b3c"
            strokeWidth="0.8"
            opacity="0.3"
          />
        </pattern>
      </defs>
      <ellipse
        cx="519"
        cy="358"
        rx="463"
        ry="16"
        fill="var(--yard-waterBottom)"
        opacity="0.15"
      />
      <path
        d="M68 271 209 225 370 237 489 211 638 228 830 215 945 253 972 300 860 342 305 351 112 329Z"
        fill="color-mix(in srgb, var(--yard-farHill) 60%, #334959)"
        stroke="#455c6d"
        strokeWidth="3"
      />
      <path
        d="M70 268 209 221 371 233 489 207 638 224 830 211 947 249"
        fill="none"
        stroke="var(--yard-groundTop)"
        strokeWidth="5"
      />
      <path
        d="M76 275Q167 220 279 272L273 326Q168 353 76 312Z"
        fill="#39535b"
      />
      {GODSWOOD_PINES.map(({ x, y }) => (
        <g key={x} transform={`translate(${x} ${y})`}>
          <path d="M0-28-12-5H-7L-17 12H17L7-5H12Z" fill="#3c5963" />
          <path
            d="M0-28-12-5 0-10 12-5M-7-2-17 12 0 5 17 12"
            fill="var(--yard-nearHill)"
          />
        </g>
      ))}
      {HALLS.map(({ x, y, width, height }) => (
        <g key={x}>
          <path
            d={`M${x} ${y}H${x + width}V${y + height}H${x}Z`}
            fill="var(--yard-farHill)"
            stroke="#435b6c"
            strokeWidth="2"
          />
          <path
            d={`M${x - 8} ${y} ${x + width / 2} ${y - 37} ${x + width + 8} ${y} ${x + width / 2} ${y + 12}Z`}
            fill="var(--yard-nearHill)"
            stroke="var(--yard-groundTop)"
            strokeWidth="3"
          />
          <path
            d={`M${x + width / 2} ${y - 34}V${y + 11}`}
            stroke="#859dab"
            strokeWidth="1.5"
          />
          <rect
            x={x + 19}
            y={y + 21}
            width="6"
            height="13"
            rx="3"
            fill="#f8ce8e"
            opacity={0.35 + nightAmount * 0.65}
          />
          <rect
            x={x + width - 26}
            y={y + 21}
            width="6"
            height="13"
            rx="3"
            fill="#f8ce8e"
            opacity={0.35 + nightAmount * 0.65}
          />
        </g>
      ))}
      {TOWERS.map((tower) => (
        <WinterfellRoundTower
          key={tower.x}
          {...tower}
          nightAmount={nightAmount}
        />
      ))}
      <path
        d="M70 276Q171 333 281 279V327Q171 375 70 324Z"
        fill="var(--yard-farHill)"
        stroke="#445c6d"
        strokeWidth="2"
      />
      <path
        d="M70 277Q171 334 281 280"
        stroke="var(--yard-groundTop)"
        strokeWidth="7"
        fill="none"
      />
      <path
        d={FRONT_WALL}
        fill="color-mix(in srgb, var(--yard-midHill) 32%, #435969)"
        stroke="#344b5b"
        strokeWidth="2"
      />
      <path d={FRONT_WALL} fill={`url(#${masonryId})`} />
      <path
        d="M274 291 482 312 735 297 953 277"
        fill="none"
        stroke="var(--yard-groundTop)"
        strokeWidth="5"
      />
      {Array.from({ length: 27 }, (_, index) => {
        const x = 282 + index * 25;
        const y = x < 482 ? 292 + (x - 273) * 0.1 : 313 - (x - 482) * 0.075;
        return (
          <path
            key={x}
            d={`M${x} ${y}v-9h11v9`}
            fill="var(--yard-farHill)"
            stroke="var(--yard-groundTop)"
            strokeWidth="2"
          />
        );
      })}
      {[484, 565].map((x) => (
        <g key={x}>
          <path
            d={`M${x - 18} 282H${x + 18}V365L${x - 18} 368Z`}
            fill="var(--yard-farHill)"
            stroke="#364d5c"
            strokeWidth="2"
          />
          <path
            d={`M${x - 20} 286v-13h9v6h8v-6h9v6h8v-6h9v13Z`}
            fill="var(--yard-nearHill)"
            stroke="var(--yard-groundTop)"
            strokeWidth="2"
          />
          <path d={`M${x - 8} 309H${x + 8}V336H${x - 8}Z`} fill="#263c4d" />
        </g>
      ))}
      <path
        d="M503 369V339a22 22 0 0 1 44 0V367Z"
        fill="#142a3b"
        stroke="#a1b3bf"
        strokeWidth="3"
      />
      <path
        d="M510 367V336M520 367V324M530 367V324M540 367V335"
        stroke="#526a7b"
        strokeWidth="2"
      />
      <WinterfellBanner x={407} y={295} />
      <WinterfellBanner x={619} y={290} />
      <path
        d="M47 361Q190 348 283 364T526 370T756 364T985 345"
        stroke="var(--yard-groundTop)"
        strokeWidth="9"
        fill="none"
      />
    </svg>
  );

  return render();
};
